import { useEffect, useState, useCallback, useRef } from 'react';
import type { Socket } from 'socket.io-client';
import { retainAppSocket, getAppSocket } from '../../../services/socketService';

export type EmailPresenceActivity = 'viewing' | 'replying' | 'idle';

export interface EmailPresenceUser {
  userId: number;
  name: string;
  activity: EmailPresenceActivity;
}

export function useEmailPresence(
  emailId: number | null | undefined,
  activity: EmailPresenceActivity = 'viewing',
): EmailPresenceUser[] {
  const [users, setUsers] = useState<EmailPresenceUser[]>([]);
  const socketRef = useRef<Socket | null>(null);

  const emitPresence = useCallback(
    (id: number, act: EmailPresenceActivity) => {
      socketRef.current?.emit('email:presence', { emailId: id, activity: act });
    },
    [],
  );

  useEffect(() => {
    if (!emailId) {
      setUsers([]);
      return undefined;
    }

    const release = retainAppSocket();
    if (!release) return undefined;

    const socket = getAppSocket();
    if (!socket) {
      release();
      return undefined;
    }
    socketRef.current = socket;

    const onPresenceUpdate = (payload: { emailId?: number; users?: EmailPresenceUser[] }) => {
      if (payload?.emailId === emailId && Array.isArray(payload.users)) {
        setUsers(payload.users);
      }
    };

    const onConnect = () => {
      emitPresence(emailId, activity);
    };

    socket.on('email:presence:update', onPresenceUpdate);
    socket.on('connect', onConnect);

    if (socket.connected) {
      emitPresence(emailId, activity);
    }

    return () => {
      emitPresence(emailId, 'idle');
      socket.off('email:presence:update', onPresenceUpdate);
      socket.off('connect', onConnect);
      release();
      socketRef.current = null;
      setUsers([]);
    };
  }, [emailId, emitPresence]);

  useEffect(() => {
    if (!emailId || !socketRef.current?.connected) return;
    emitPresence(emailId, activity);
  }, [emailId, activity, emitPresence]);

  return users;
}
